import { createContext, Dispatch, DispatchWithoutAction, SetStateAction, useCallback, useMemo } from 'react'
import { PositionRange } from '../../types'
import { CutterState } from '../../state/CutterState'
import { useObservableState } from '../../hooks/useObservableState'
import { CutterAudio } from '../../state/entities/CutterAudio'

export interface CutterContextValue {
  audioBuffer: AudioBuffer
  filename: string
  audio: CutterAudio
  visibleRange: PositionRange
  setVisibleRange: Dispatch<SetStateAction<PositionRange>>
  selection: PositionRange | null
  setSelection: Dispatch<SetStateAction<PositionRange | null>>
  undoAvailable: boolean
  redoAvailable: boolean
  undo: DispatchWithoutAction
  redo: DispatchWithoutAction
  remove: DispatchWithoutAction
  crop: DispatchWithoutAction
}

export const CutterContext = createContext<CutterContextValue>({
  audioBuffer: {} as AudioBuffer,
  filename: '',
  audio: {} as CutterAudio,
  visibleRange: [0, 1],
  setVisibleRange() {},
  selection: null,
  setSelection() {},
  undoAvailable: false,
  redoAvailable: false,
  undo() {},
  redo() {},
  remove() {},
  crop() {},
})

export function useCutterContext(audioBuffer: AudioBuffer, filename: string): CutterContextValue {
  const state = useMemo(() => new CutterState(audioBuffer), [audioBuffer])
  const [audio] = useObservableState(state.audio)
  const [visibleRange, setVisibleRange] = useObservableState(state.visibleRange)
  const [selection, setSelection] = useObservableState(state.selection)
  const [prevSnapshots] = useObservableState(state.prevSnapshots)
  const [nextSnapshots] = useObservableState(state.nextSnapshots)
  const setSnapshotSelection = useCallback(
    (value: SetStateAction<PositionRange | null>) => {
      state.commit()
      setSelection(value)
    },
    [state, setSelection],
  )
  const undoAvailable = useMemo(() => !!prevSnapshots.length, [prevSnapshots])
  const redoAvailable = useMemo(() => !!nextSnapshots.length, [nextSnapshots])

  return useMemo(
    () => ({
      audioBuffer,
      filename,
      audio,
      visibleRange,
      setVisibleRange,
      selection,
      setSelection: setSnapshotSelection,
      undoAvailable,
      redoAvailable,
      undo: state.undo,
      redo: state.redo,
      remove: state.remove,
      crop: state.crop,
    }),
    [
      audioBuffer,
      filename,
      audio,
      visibleRange,
      setVisibleRange,
      selection,
      setSnapshotSelection,
      undoAvailable,
      redoAvailable,
      state,
    ],
  )
}
